/**
 * Utilities for deriving sync status panel mode and filtering empty UI tracks.
 */

export function hasVisibleNodeChanges(nodeData) {
    if (!nodeData) return false;
    return (
        (nodeData.foldersToAdd || 0) > 0 ||
        (nodeData.itemsToAdd || 0) > 0 ||
        (nodeData.foldersToUpdate || 0) > 0 ||
        (nodeData.itemsToUpdate || 0) > 0 ||
        (nodeData.foldersToRemove || 0) > 0 ||
        (nodeData.itemsToRemove || 0) > 0
    );
}

export function hasVisiblePluginContent(pluginData) {
    if (!pluginData || typeof pluginData !== 'object') return false;
    return Object.values(pluginData).some((info) => {
        if (!info || typeof info !== 'object') return false;
        return Boolean(info.summary) || (info.messages && info.messages.length > 0);
    });
}

export function hasAnyStatisticsChanges(statistics, pluginData) {
    if (!statistics) return false;
    if ((statistics.nodesToAdd || 0) > 0 || (statistics.nodesToRemove || 0) > 0) return true;
    const byNode = statistics.byNode || {};
    for (const nodeData of Object.values(byNode)) {
        if (hasVisibleNodeChanges(nodeData)) return true;
    }
    if (hasVisiblePluginContent(pluginData)) return true;
    return false;
}

export function computeTotalsFromStatistics(statistics, tense = 'past') {
    const byNode = (statistics || {}).byNode || {};
    const totalCreated = Object.values(byNode).reduce(
        (sum, node) => sum + (node.foldersToAdd || 0) + (node.itemsToAdd || 0), 0
    );
    const totalUpdated = Object.values(byNode).reduce(
        (sum, node) => sum + (node.foldersToUpdate || 0) + (node.itemsToUpdate || 0), 0
    );
    const totalDeleted = Object.values(byNode).reduce(
        (sum, node) => sum + (node.foldersToRemove || 0) + (node.itemsToRemove || 0), 0
    );
    const createdLabel = tense === 'future' ? 'to create' : 'created';
    const updatedLabel = tense === 'future' ? 'to update' : 'updated';
    const deletedLabel = tense === 'future' ? 'to remove' : 'deleted';

    let text = `Created: ${totalCreated} | Updated: ${totalUpdated} | Deleted: ${totalDeleted}`;
    text = text
        .replace('Created:', tense === 'future' ? 'To create:' : 'Created:')
        .replace('Updated:', tense === 'future' ? 'To update:' : 'Updated:')
        .replace('Deleted:', tense === 'future' ? 'To remove:' : 'Deleted:');

    return { totalCreated, totalUpdated, totalDeleted, summaryText: text, createdLabel, updatedLabel, deletedLabel };
}

/**
 * Derive panel display mode from sync record, diff review state, and errors.
 */
export function deriveSyncPanelMode({
    syncRecord,
    diffReview,
    syncError,
    isSyncInProgress,
}) {
    if (syncError) return 'error';
    if (diffReview) return 'diffReview';
    const status = syncRecord?.status;
    if (status === 'error') return 'error';
    if (status === 'pending' || status === 'in_progress' || isSyncInProgress) return 'syncing';
    if (status === 'completed') {
        const stats = syncRecord.statistics || {};
        const plugin = syncRecord.pluginData || {};
        if (!hasAnyStatisticsChanges(stats, plugin)) return 'noop';
        return 'complete';
    }
    if (status === 'never_synced') return 'idle';
    return 'idle';
}

export const SYNC_MODES = {
    FULL: 'full',
    SELECTIVE: 'selective',
};

export function loadSyncMode(connectionId) {
    try {
        const stored = localStorage.getItem(`bdsa-sync-mode:${connectionId}`);
        if (stored === SYNC_MODES.FULL || stored === SYNC_MODES.SELECTIVE) return stored;
    } catch (_e) { /* ignore */ }
    return SYNC_MODES.FULL;
}

export function saveSyncMode(connectionId, mode) {
    try {
        if (mode === SYNC_MODES.FULL || mode === SYNC_MODES.SELECTIVE) {
            localStorage.setItem(`bdsa-sync-mode:${connectionId}`, mode);
        }
    } catch (_e) { /* ignore */ }
}
